import { UnknownSenderBar } from "./Report";
import { LockedGate } from "./ChatLock";
import { appearance, wallClass } from "./Appearance";
import { CallBubble, CallButtons } from "./Call";
import { useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import * as S from "../state";
import { dayLabel, lastSeenLabel, sameDay } from "../util";
import { summarize } from "../db";
import { Avatar } from "./Avatar";
import { Bubble } from "./Bubble";
import { replyAfterOpen, replyTo, resetChatUI, sendingFiles } from "./chatui";
import { Composer } from "./Composer";
import { ChatDialogs } from "./Dialogs";
import { ShareDialogs } from "./Share";
import { Icon } from "./Icon";
import { engine, infoOpen } from "./Main";
import {
  ChannelFooter,
  ChannelStatusLine,
  preview,
  useChannel,
} from "./Updates";
import { BroadcastIcon, BroadcastStatusLine } from "./Communities";

export function ChatView({ jid }: { jid: string }) {
  void appearance.value; // re-render when a chat background changes
  const name = S.displayName(jid);
  const group = S.isGroup(jid);
  const channel = S.isChannel(jid);
  const broadcast = S.isBroadcast(jid);
  const chanInfo = channel
    ? (S.channels.value.get(jid) ?? preview.value.get(jid))
    : undefined;
  const avatar = group
    ? S.groups.value.get(jid)?.avatar_url
    : channel
      ? chanInfo?.avatar_url
      : S.profiles.value.get(jid)?.avatar_url;
  const [dragging, setDragging] = useState(false);
  const chanAdmin =
    chanInfo?.my_role === "owner" || chanInfo?.my_role === "admin";
  const post = group
    ? S.canPost(jid)
    : channel
      ? { ok: chanAdmin }
      : { ok: true };
  useChannel(channel ? jid : "");
  const isBlocked = !group && S.blocked.value.has(jid);
  const ttl = S.chatTTL(jid);
  useEffect(() => {
    // A private reply started from a group: quote it here (after the
    // previous chat's state was reset).
    const p = replyAfterOpen.value;
    if (p?.chat === jid) {
      replyAfterOpen.value = null;
      replyTo.value = p.m;
    }
    return resetChatUI;
  }, [jid]);

  return (
    <section
      class={"chat " + wallClass(jid)}
      aria-label={`Chat with ${name}`}
      onDragOver={(e) => {
        if (post.ok && e.dataTransfer?.types.includes("Files")) {
          e.preventDefault();
          setDragging(true);
        }
      }}
      onDragLeave={(e) => e.currentTarget === e.target && setDragging(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragging(false);
        const files = [...(e.dataTransfer?.files ?? [])];
        if (files.length) sendingFiles.value = files;
      }}
    >
      <header class="bar chat-bar">
        <button
          class="icon-btn back-btn"
          onClick={() => void engine.value?.openChat(null)}
          aria-label="Back to chats"
        >
          <Icon name="back" />
        </button>
        <button
          class="chat-bar-who"
          onClick={() => (infoOpen.value = !infoOpen.value)}
          aria-label={group ? "Group info" : "Contact info"}
        >
          {broadcast ? (
            <BroadcastIcon size={40} />
          ) : (
            <Avatar
              url={avatar}
              jid={jid}
              name={name}
              size={40}
              group={group || channel}
            />
          )}
          <div class="chat-bar-text">
            <span class="chat-name">
              {name}
              {ttl > 0 && (
                <span
                  class="muted-icon"
                  title={`Disappearing messages: ${S.ttlLabel(ttl)}`}
                >
                  <Icon name="timer" size={14} />
                </span>
              )}
              {S.isMuted(jid) && (
                <span class="muted-icon" title="Muted">
                  <Icon name="bellOff" size={14} />
                </span>
              )}
            </span>
            {group ? (
              <GroupStatusLine jid={jid} />
            ) : channel ? (
              <ChannelStatusLine jid={jid} />
            ) : broadcast ? (
              <BroadcastStatusLine jid={jid} />
            ) : (
              <StatusLine jid={jid} />
            )}
          </div>
        </button>
        {!channel && !broadcast && <CallButtons jid={jid} />}
      </header>
      {S.lockedChats.value.has(jid) && !S.lockOpen.value ? (
        <LockedGate jid={jid} />
      ) : (
        <>
          <PinnedBar jid={jid} />
      <UnknownSenderBar jid={jid} />
          <Messages jid={jid} />
          {isBlocked ? (
            <footer class="composer-blocked">
              You blocked this contact.{" "}
              <button
                class="link"
                onClick={() => void engine.value?.setBlocked(jid, false)}
              >
                Unblock
              </button>
            </footer>
          ) : channel && !post.ok ? (
            <ChannelFooter jid={jid} />
          ) : post.ok ? (
            <Composer key={jid} jid={jid} />
          ) : (
            <footer class="composer-blocked">{post.reason}</footer>
          )}
        </>
      )}
      {dragging && (
        <div class="drop-zone">
          <Icon name="document" size={48} />
          <p>Drop files to send</p>
        </div>
      )}
      <ChatDialogs jid={jid} />
      <ShareDialogs jid={jid} />
    </section>
  );
}

function GroupStatusLine({ jid }: { jid: string }) {
  const who = S.typingWho.value.get(jid);
  if (S.isTyping(jid) && who)
    return <span class="status typing">{S.memberName(who)} is typing…</span>;
  const g = S.groups.value.get(jid);
  if (!g) return <span class="status">click here for group info</span>;
  const me = S.myJid.value;
  const names = g.members
    .map((m) => m.jid)
    .sort((a, b) =>
      a === me
        ? 1
        : b === me
          ? -1
          : S.memberName(a).localeCompare(S.memberName(b)),
    )
    .map((j) => S.memberName(j));
  return <span class="status">{names.join(", ")}</span>;
}

function StatusLine({ jid }: { jid: string }) {
  if (S.isTyping(jid)) return <span class="status typing">typing…</span>;
  if (S.isOnline(jid)) return <span class="status">online</span>;
  const seen = S.lastSeen.value.get(jid);
  if (seen) return <span class="status">{lastSeenLabel(seen)}</span>;
  const push = S.pushName(jid);
  return push ? (
    <span class="status">~{push}</span>
  ) : (
    <span class="status">click here for contact info</span>
  );
}

function Messages({ jid }: { jid: string }) {
  const list = S.messages.value;
  const scroller = useRef<HTMLDivElement>(null);
  const stick = useRef(true); // pinned to bottom?
  const prevFirst = useRef<string | undefined>(undefined);
  const prevHeight = useRef(0);
  const loading = useRef(false);
  const [showJump, setShowJump] = useState(false);
  // "3 unread messages" above the first one you haven't seen; it stays while
  // the chat is open (also after they're read), like WhatsApp.
  const unread = S.chats.value.find((c) => c.jid === jid)?.unread ?? 0;
  const [divider, setDivider] = useState<{ key: string; n: number } | null>(
    null,
  );
  useEffect(() => setDivider(null), [jid]);
  useEffect(() => {
    if (!unread) return;
    const incoming = list.filter(
      (m) => m.dir === "in" && m.kind !== "system" && !m.hidden,
    );
    const first = incoming[Math.max(0, incoming.length - unread)];
    if (first && (!divider || unread > divider.n))
      setDivider({ key: first.key, n: unread });
  }, [unread, list.length]);

  // Keep the view anchored: stay at the bottom for new messages, keep the
  // reading position when older messages are prepended.
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const first = list[0]?.key;
    if (S.focusMessage.value) {
      // the focused bubble scrolls itself into view
    } else if (
      prevFirst.current &&
      first !== prevFirst.current &&
      prevHeight.current
    ) {
      el.scrollTop += el.scrollHeight - prevHeight.current;
    } else if (stick.current) {
      el.scrollTop = el.scrollHeight;
    }
    prevFirst.current = first;
    prevHeight.current = el.scrollHeight;
  }, [list]);

  useEffect(() => {
    stick.current = !S.focusMessage.value;
  }, [jid]);

  // Images load after layout; keep the bottom pinned while they do.
  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const ro = new ResizeObserver(() => {
      if (stick.current) el.scrollTop = el.scrollHeight;
      prevHeight.current = el.scrollHeight;
    });
    ro.observe(el.firstElementChild!);
    return () => ro.disconnect();
  }, []);

  async function onScroll() {
    const el = scroller.current!;
    const fromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    stick.current = fromBottom < 40;
    setShowJump(fromBottom > 400);
    prevHeight.current = el.scrollHeight;
    if (el.scrollTop < 200 && !loading.current && S.hasOlder.value) {
      loading.current = true;
      try {
        await engine.value?.loadOlder();
      } finally {
        loading.current = false;
      }
    }
  }

  return (
    <div
      class="messages"
      ref={scroller}
      onScroll={onScroll}
      role="log"
      aria-live="polite"
    >
      <div class="messages-inner">
        {!S.hasOlder.value && (
          <div class="e2e-note">
            {S.isChannel(jid) ? (
              <>
                This channel is public. Admins' phone numbers aren't shown to
                followers, and followers' numbers aren't shown to admins.
              </>
            ) : S.isBroadcast(jid) ? (
              <>
                <Icon name="lock" size={12} /> Broadcast messages reach each
                recipient as a normal, end-to-end encrypted message.
              </>
            ) : (
              <>
                <Icon name="lock" size={12} /> Messages are end-to-end
                encrypted. No one outside of this chat, not even xmm, can read
                them.
              </>
            )}
          </div>
        )}
        {list.map((m, i) => {
          const prev = list[i - 1];
          const newDay = !prev || !sameDay(prev.ts, m.ts);
          // A new "run" starts when the author changes (groups show the name then).
          const tail =
            newDay ||
            prev.dir !== m.dir ||
            prev.from !== m.from ||
            prev.kind === "system";
          return (
            <>
              {newDay && (
                <div class="day" key={"d" + m.key}>
                  <span>{dayLabel(m.ts)}</span>
                </div>
              )}
              {divider?.key === m.key && (
                <div class="unread-divider" key={"u" + m.key} role="separator">
                  <span>
                    {divider.n} unread message{divider.n > 1 ? "s" : ""}
                  </span>
                </div>
              )}
              {m.kind === "system" ? (
                <div
                  class={
                    "system-msg" +
                    (m.system?.type === "security" ? " security" : "")
                  }
                  key={m.key}
                  data-key={m.key}
                >
                  <span
                    onClick={() =>
                      m.system?.type === "security" && (infoOpen.value = true)
                    }
                  >
                    {m.system?.type === "security" && (
                      <Icon name="lock" size={12} />
                    )}
                    {S.systemText(m.system!, m.body)}
                  </span>
                </div>
              ) : m.kind === "call" ? (
                <CallBubble key={m.key} m={m} tail={tail} />
              ) : (
                <Bubble key={m.key} m={m} tail={tail} />
              )}
            </>
          );
        })}
      </div>
      {showJump && (
        <button
          class="jump"
          aria-label="Scroll to latest"
          onClick={() => {
            const el = scroller.current!;
            el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
          }}
        >
          <Icon name="down" />
        </button>
      )}
    </div>
  );
}

/** Pinned messages: the newest on top; tap to jump, the chevron cycles. */
function PinnedBar({ jid }: { jid: string }) {
  const pins = S.pinned.value.filter(
    (m) => m.chat === jid && m.pin && m.pin.until > Date.now(),
  );
  const [at, setAt] = useState(0);
  if (!pins.length) return null;
  const m = pins[Math.min(at, pins.length - 1)];
  const jump = () => {
    if (document.querySelector(`[data-key="${CSS.escape(m.key)}"]`))
      S.focusMessage.value = m.key;
    else void engine.value?.openChat(jid, m);
    setAt((at + 1) % pins.length); // the next tap shows the next pin
  };
  return (
    <div class="pinned-bar" role="region" aria-label="Pinned messages">
      <span class="pinned-dots" aria-hidden="true">
        {pins.map((p) => (
          <span key={p.key} class={p.key === m.key ? "on" : ""} />
        ))}
      </span>
      <Icon name="pin" size={16} />
      <button
        class="pinned-text"
        onClick={jump}
        aria-label={`Pinned message: ${summarize(m)}`}
      >
        <span class="ellipsis">{summarize(m) || "Message"}</span>
      </button>
      {engine.value?.canPin(jid) && (
        <button
          class="icon-btn small"
          aria-label="Unpin"
          title="Unpin"
          onClick={() => void engine.value?.pinMessage(m, 0)}
        >
          <Icon name="close" size={16} />
        </button>
      )}
    </div>
  );
}
